/**
 * MoveShell — persistent two-column layout for the unified move view.
 *
 * Mirrors the QuoteShell pattern:
 *   • md+: sticky left panel (hero card + nav) | scrolling right panel (<Outlet />)
 *   • mobile: full-screen sub-page content only
 *
 * All /moves/:quoteId/* routes are nested under this shell so the hero card
 * and navigation persist across sub-page navigations.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NavLink,
  Outlet,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  Activity,
  Box,
  Calendar,
  Camera,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FlaskConical,
  Footprints,
  Home,
  LayoutGrid,
  ListChecks,
  Package,
  Trash2,
  Users,
  UserCircle,
  Wrench,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { QuoteContextProvider, type QuoteContextValue } from '../../context/QuoteContext';
import { OrgInventoryErrorBanner } from '../ui/OrgInventoryErrorBanner';
import { useQuote } from '../../hooks/useQuote';
import { useOrgAdminUsers } from '../../hooks/useOrgAdminUsers';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgWarehouses } from '../../hooks/useOrgWarehouses';
import { useOrgTrucks } from '../../hooks/useOrgTrucks';
import { useOrgInventory } from '../../hooks/useOrgInventory';
import { useOrgRates } from '../../hooks/useOrgRates';
import { useResidentialFlatRates } from '../../hooks/useResidentialFlatRates';
import { useFormulaEngine } from '../../hooks/useFormulaEngine';
import { extractOrgTruckTypes } from '../../utils/formulaCalculation';
import { useJob } from '../../hooks/useJobs';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { api } from '../../lib/api';
import { IssueLogModal, type IssueFormData } from '../../components/jobs/IssueLogModal';
import { TruckAssignmentModal } from '../../components/jobs/TruckAssignmentModal';
import { SignOffCaptureModal, WaiveSignOffModal, SignOffDetailModal } from '../../components/jobs/SignOffModal';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { usePaymentRequest } from '../../hooks/usePaymentRequest';
import {
  LifecycleStage,
  LIFECYCLE_STAGE_LABEL,
  LeadStatus,
  QuoteStatus,
  JobStatus,
  JOB_STAGES,
  JOB_STAGE_LABEL,
  JOB_STAGE_ACTION,
  OrgUser,
  QuotePatch,
} from '../../types/admin';
import {
  HeroCard,
  BottomSheet,
  StatusSheet,
  UserPickerSheet,
  EstimateScheduledSheet,
  CommunicationLogSheet,
  RequestPaymentSheet,
} from '../../pages/unified/MovePageComponents';

// ── Shell ──────────────────────────────────────────────────────────────────────

export function MoveShell() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;

  // Shared quote state — provided via context to all sub-pages
  const quoteData = useQuote(orgId, quoteId ?? null);
  const { quote, loading, updateQuote, calculateTransit, calculateWarehouseTransits, refetch } = quoteData;

  // Fetch job data when the quote is in the job stage (by explicit jobStatus OR lifecycleStage)
  const jobQuoteId = (quote?.jobStatus || quote?.lifecycleStage === 'job') ? (quoteId ?? null) : null;
  const { job, logJobIssue, updateJobStatus, updateJobStage, updateJobTrucks, checkOutJobTruck, checkInJobTruck, refetch: refetchJob } = useJob(orgId, jobQuoteId);

  // Start Job handler
  const [startingJob, setStartingJob] = useState(false);
  const handleStartJob = useCallback(async () => {
    if (!orgId || !quoteId) return;
    setStartingJob(true);
    try {
      await updateJobStatus('in_progress');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[start job]', err);
    } finally {
      setStartingJob(false);
    }
  }, [orgId, quoteId, updateJobStatus, refetchJob, refetch]);

  // ── Job in-progress state ───────────────────────────────────────────────
  const [advancing, setAdvancing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [confirmAdvance, setConfirmAdvance] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);

  // Sign-off state
  const [showSignOffCapture, setShowSignOffCapture] = useState(false);
  const [showWaiveSignOff, setShowWaiveSignOff] = useState(false);
  const [showSignOffDetail, setShowSignOffDetail] = useState(false);
  const [signOffSubmitting, setSignOffSubmitting] = useState(false);
  const [signOffError, setSignOffError] = useState<string | null>(null);

  // Payment request state
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);
  const { sending: paymentSending, sendRequest: sendPaymentRequest } = usePaymentRequest(orgId, quoteId ?? null);

  // ── Stage advance/revert handlers ───────────────────────────────────────
  const handleAdvance = useCallback(async () => {
    setConfirmAdvance(false);
    setAdvancing(true);
    try {
      await updateJobStage('advance');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[advance stage]', err);
    } finally {
      setAdvancing(false);
    }
  }, [updateJobStage, refetchJob, refetch]);

  const handleRevert = useCallback(async () => {
    setConfirmRevert(false);
    setReverting(true);
    try {
      await updateJobStage('revert');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[revert stage]', err);
    } finally {
      setReverting(false);
    }
  }, [updateJobStage, refetchJob, refetch]);

  // ── Sign-off handlers ──────────────────────────────────────────────────
  const handleRequestSignOff = useCallback(() => {
    setShowSignOffCapture(true);
  }, []);

  const handleSignOffCapture = useCallback(async (signerName: string, signatureDataUrl: string) => {
    if (!orgId || !quoteId) return;
    setSignOffSubmitting(true);
    setSignOffError(null);
    try {
      await api.patch(`/jobs/${orgId}/${quoteId}/signoff`, {
        action: 'capture',
        signerName,
        signatureImage: signatureDataUrl,
      });
      setShowSignOffCapture(false);
      setSignOffError(null);
      const currentStage = job?.jobStage ?? 'scheduled';
      if (currentStage === 'customer_sign_off') {
        try { await updateJobStage('advance'); } catch (err) { console.error('[signoff advance]', err); }
      }
      await refetchJob();
      await refetch();
      if ((job?.paymentSummary?.outstandingAmountCents ?? 0) > 0) {
        setShowPaymentSheet(true);
      }
    } catch (err) {
      console.error('[signoff capture]', err);
      setSignOffError(err instanceof Error ? err.message : 'Failed to save sign-off. Please try again.');
    }
    setSignOffSubmitting(false);
  }, [orgId, quoteId, job?.jobStage, job?.paymentSummary?.outstandingAmountCents, updateJobStage, refetchJob, refetch]);

  const handleSignOffWaive = useCallback(async (reason: string) => {
    if (!orgId || !quoteId) return;
    setSignOffSubmitting(true);
    try {
      await api.patch(`/jobs/${orgId}/${quoteId}/signoff`, {
        action: 'waive',
        waiveReason: reason,
      });
      setShowWaiveSignOff(false);
      const currentStage = job?.jobStage ?? 'scheduled';
      if (currentStage === 'customer_sign_off') {
        await updateJobStage('advance');
      }
      await refetchJob();
      await refetch();
      if ((job?.paymentSummary?.outstandingAmountCents ?? 0) > 0) {
        setShowPaymentSheet(true);
      }
    } catch (err) {
      console.error('[signoff waive]', err);
    }
    setSignOffSubmitting(false);
  }, [orgId, quoteId, job?.jobStage, job?.paymentSummary?.outstandingAmountCents, updateJobStage, refetchJob, refetch]);

  // ── Payment request handler ─────────────────────────────────────────────
  const handleSendPaymentRequest = useCallback(async (channels: ('sms' | 'email')[]) => {
    await sendPaymentRequest(channels);
    setShowPaymentSheet(false);
  }, [sendPaymentRequest]);

  const handleReconciled = useCallback(() => {
    refetchJob();
  }, [refetchJob]);

  // Fetch quote version count (for Client Quotes button text)
  const [versionsCount, setVersionsCount] = useState(0);
  useEffect(() => {
    if (!orgId || !quoteId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: unknown[] }>(
          `/quotes/${orgId}/${quoteId}/versions`,
        );
        if (!cancelled) setVersionsCount(res.data.length);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [orgId, quoteId]);

  // Org users for lead/move lead pickers
  const { users: orgUsers, loading: usersLoading } = useOrgAdminUsers(orgId ?? '');

  // Org settings
  const { org: orgSettings, loading: orgSettingsLoading } = useOrgSettings(orgId ?? '');

  // Data hooks for formula engine
  const { items: orgItems, loading: orgItemsLoading, error: orgItemsError } = useOrgInventory(orgId ?? '');
  const { rates: orgRates, loading: orgRatesLoading } = useOrgRates(orgId ?? '');
  const { flatRates: orgFlatRates, loading: orgFlatRatesLoading } = useResidentialFlatRates(orgId ?? '');

  // Warehouses for background transit calculation
  const { warehouses } = useOrgWarehouses(orgId ?? '');
  const { trucks: fleetTrucks, refetch: refetchTrucks } = useOrgTrucks(orgId ?? '');
  const availableTruckTypes = useMemo(() => extractOrgTruckTypes(fleetTrucks), [fleetTrucks]);

  // Formula engine lives in the shell so it stays mounted across all
  // sub-page navigations and reacts immediately to any input change.
  useFormulaEngine({
    quote,
    orgItems,
    orgItemsLoading,
    orgRates,
    orgRatesLoading,
    org: orgSettings,
    orgSettingsLoading,
    updateQuote,
    orgFlatRates,
    orgFlatRatesLoading,
    availableTruckTypes,
  });

  const warehousesRef = useRef(warehouses);
  warehousesRef.current = warehouses;

  const handleCheckOutTruck = useCallback(async (truckId: string) => {
    try {
      const result = await checkOutJobTruck(truckId);
      await refetchTrucks();
      if (result?.stageAdvanced) {
        await refetchJob();
        await refetch();
      }
    } catch (err) {
      console.error('[truck checkout]', err);
    }
  }, [checkOutJobTruck, refetchTrucks, refetchJob, refetch]);

  const handleCheckInTruck = useCallback(async (truckId: string, warehouseId: string) => {
    try {
      const result = await checkInJobTruck(truckId, warehouseId);
      await refetchTrucks();
      if (result.stageAdvanced) {
        await refetchJob();
        await refetch();
      }
      return result;
    } catch (err) {
      console.error('[truck checkin]', err);
      throw err;
    }
  }, [checkInJobTruck, refetchTrucks, refetchJob, refetch]);

  const stage: LifecycleStage = quote?.lifecycleStage ?? (quote?.jobStatus ? 'job' : quote?.leadStatus ? 'lead' : 'quote');

  // Modal state
  const [showStatusSheet, setShowStatusSheet] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [userPickerMode, setUserPickerMode] = useState<'quoteLead' | 'moveLead'>('quoteLead');
  const [showEstimateSheet, setShowEstimateSheet] = useState(false);
  const [showActivitySheet, setShowActivitySheet] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showHeroTruckAssign, setShowHeroTruckAssign] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Save helper
  const save = useCallback(async (patch: QuotePatch) => {
    try { await updateQuote(patch); } catch (err) { console.error('[move shell save]', err); }
  }, [updateQuote]);

  // ── Transit auto-calculation ────────────────────────────────────────────
  const originPlaceId = quote?.addresses?.origin?.placeId;
  const destPlaceId = quote?.addresses?.destination?.placeId;
  const [, setCalculatingTransit] = useState(false);
  const lastTransitKeyRef = useRef('');

  useEffect(() => {
    if (!originPlaceId || !destPlaceId) return;
    const key = `${originPlaceId}|${destPlaceId}`;
    if (key === lastTransitKeyRef.current) return;
    lastTransitKeyRef.current = key;

    setCalculatingTransit(true);
    calculateTransit(originPlaceId, destPlaceId)
      .then(() => {
        setCalculatingTransit(false);
        const wh = warehousesRef.current;
        if (wh.length) {
          calculateWarehouseTransits(
            wh.map(w => ({ id: w.id, placeId: w.placeId })),
            originPlaceId,
            destPlaceId,
          ).catch(err => console.error('[warehouse transit]', err));
        }
      })
      .catch(err => {
        lastTransitKeyRef.current = '';
        setCalculatingTransit(false);
        console.error('[transit calc]', err);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originPlaceId, destPlaceId, calculateTransit, calculateWarehouseTransits]);

  // ── Auto-calculate estimated value when inputs change ─────────────────
  const lastEstimateKeyRef = useRef('');
  useEffect(() => {
    if (!quote || !orgId) return;
    const hasOrigin = !!quote.addresses?.origin?.placeId || !!quote.originZipCode;
    const hasDest = !!quote.addresses?.destination?.placeId || !!quote.destinationZipCode;
    if (!hasOrigin || !hasDest) return;

    const key = [
      quote.type,
      quote.addresses?.origin?.placeId ?? '',
      quote.originZipCode ?? '',
      quote.addresses?.destination?.placeId ?? '',
      quote.destinationZipCode ?? '',
      quote.numberOfBedrooms ?? '',
      quote.squareFootage ?? '',
      quote.numberOfEmployees ?? '',
      quote.originStories ?? '',
      quote.destinationStories ?? '',
      quote.originElevatorAccess ?? '',
      quote.destinationElevatorAccess ?? '',
    ].join('|');

    if (key === lastEstimateKeyRef.current) return;
    lastEstimateKeyRef.current = key;

    const timer = setTimeout(() => {
      api.post<{ success: boolean; data: { estimatedValue: number | null } }>(
        `/quotes/${orgId}/${quote.id}/calculate-lead-estimate`,
      )
        .then(() => refetch())
        .catch(err => console.error('[lead estimate]', err));
    }, 800);
    return () => clearTimeout(timer);
  }, [
    quote?.type, quote?.id, orgId, refetch,
    quote?.addresses?.origin?.placeId, quote?.originZipCode,
    quote?.addresses?.destination?.placeId, quote?.destinationZipCode,
    quote?.numberOfBedrooms, quote?.squareFootage, quote?.numberOfEmployees,
    quote?.originStories, quote?.destinationStories,
    quote?.originElevatorAccess, quote?.destinationElevatorAccess,
    quote,
  ]);

  // Status change handler
  const handleStatusChange = useCallback(async (status: string) => {
    if (!quote) return;
    if (stage === 'lead') {
      await updateQuote({ leadStatus: status as LeadStatus });
    } else if (stage === 'quote') {
      await updateQuote({ quoteStatus: status as QuoteStatus });
    } else {
      await updateQuote({ jobStatus: status as JobStatus });
      // Refetch job data so hero card and progress card update
      await refetchJob();
    }
    await refetch();
  }, [quote, stage, updateQuote, refetchJob, refetch]);

  // Estimate scheduled handler
  const handleEstimateScheduled = useCallback(async (estimateDate: string, estimatorUid: string, _estimatorName: string) => {
    setShowEstimateSheet(false);
    try {
      const patch: Record<string, unknown> = {
        leadStatus: 'estimate_scheduled',
        lifecycleStage: 'quote',
        quoteStatus: 'draft',
      };
      if (estimateDate) patch.estimateVisitDate = estimateDate;
      if (estimatorUid) patch.quoteLeadUserId = estimatorUid;
      await updateQuote(patch as Parameters<typeof updateQuote>[0]);
      navigate(`/moves/${quoteId}`);
    } catch (err) {
      console.error('[estimate scheduled]', err);
    }
  }, [updateQuote, navigate, quoteId]);

  // Lead assignment handler
  const handleLeadAssign = useCallback(async (user: OrgUser) => {
    if (userPickerMode === 'quoteLead') {
      await updateQuote({ quoteLeadUserId: user.uid });
    } else {
      await updateQuote({ moveLeadUserId: user.uid });
    }
  }, [userPickerMode, updateQuote]);

  const openLeadPicker = useCallback(() => {
    setUserPickerMode(stage === 'job' ? 'moveLead' : 'quoteLead');
    setShowUserPicker(true);
  }, [stage]);

  // Current status for the status sheet
  const currentStatus = stage === 'lead'
    ? (quote?.leadStatus ?? 'new')
    : stage === 'quote'
      ? (quote?.quoteStatus ?? 'draft')
      : (quote?.jobStatus ?? 'scheduled');

  // Current lead UID for the user picker
  const currentLeadUid = stage === 'job'
    ? quote?.moveLeadUserId
    : quote?.quoteLeadUserId ?? quote?.createdBy;

  // Filter users for move lead picker (crew-eligible roles: movers, admins, field leads)
  const pickerUsers = stage === 'job'
    ? orgUsers.filter(u => u.status === 'active' && (u.role === 'mover' || u.role === 'admin' || u.role === 'field_lead'))
    : orgUsers.filter(u => u.status === 'active');

  // Delete handler
  async function handleDeleteQuote() {
    if (!orgId || !quote) return;
    setDeleting(true);
    try {
      await api.del(`/quotes/${orgId}/${quote.id}`);
      navigate('/');
    } catch (err) {
      console.error('[delete quote]', err);
      setDeleting(false);
    }
  }

  // Demo / admin flags (for formula details visibility)
  const isOrgAdmin = activeOrg?.role === 'admin' || activeOrg?.role === 'field_lead';
  const isDemo = orgSettings?.isDemo ?? false;

  // Nav items for the left panel
  const navItems = [
    ...(stage !== 'lead' ? [
      { to: `/moves/${quoteId}/customer-lead`, icon: <UserCircle size={15} />, label: 'Customer & Lead Details' },
      // Payments is part of the Customer Payments feature; hide it unless the org has it enabled.
      ...(activeOrg?.featureCustomerPayments ? [
        { to: `/moves/${quoteId}/payments`, icon: <CreditCard size={15} />, label: 'Payments' },
      ] : []),
      ...(stage === 'job' ? [
        { to: `/moves/${quoteId}/crew-trucks`, icon: <Users size={15} />, label: 'Crew & Trucks' },
      ] : []),
      { to: `/moves/${quoteId}/dates-logistics`, icon: <Calendar size={15} />, label: 'Key Dates & Addresses' },
      ...(quote?.type === 'commercial' ? [
        { to: `/moves/${quoteId}/access-logistics`, icon: <Footprints size={15} />, label: 'Logistics' },
        { to: `/moves/${quoteId}/casegoods`, icon: <Box size={15} />, label: 'Casegoods Inventory' },
        { to: `/moves/${quoteId}/modular-workstations`, icon: <LayoutGrid size={15} />, label: 'Modular Workstations' },
        { to: `/moves/${quoteId}/materials`, icon: <Package size={15} />, label: 'Materials' },
        { to: `/moves/${quoteId}/accessorial-services`, icon: <Wrench size={15} />, label: 'Accessorial Services' },
      ] : []),
      ...(quote?.type === 'residential' ? [
        { to: `/moves/${quoteId}/access-property`, icon: <Home size={15} />, label: 'Property Details' },
        { to: `/moves/${quoteId}/residential-rooms`, icon: <Box size={15} />, label: 'Rooms & Inventory' },
        { to: `/moves/${quoteId}/materials`, icon: <Package size={15} />, label: 'Materials' },
      ] : []),
      { to: `/moves/${quoteId}/scope-of-services`, icon: <ListChecks size={15} />, label: 'Scope of Services' },
      { to: `/moves/${quoteId}/photos-notes`, icon: <Camera size={15} />, label: 'Photos & Notes' },
    ] : []),
    ...(stage !== 'lead' && isDemo && isOrgAdmin
      ? [{ to: `/moves/${quoteId}/formula`, icon: <FlaskConical size={15} />, label: 'Formula Details' }]
      : []),
    { to: `/moves/${quoteId}/history`, icon: <Activity size={15} />, label: 'History' },
  ];

  return (
    <QuoteContextProvider value={{
      ...quoteData,
      orgItems,
      orgItemsLoading,
      orgItemsError,
      // Expose extra state for sub-pages via context
    } as QuoteContextValue & { save?: typeof save; calculatingTransit?: boolean; orgSettings?: typeof orgSettings }}>
      <div className="md:grid md:grid-cols-[320px_1fr] lg:grid-cols-[360px_1fr]">

        {/* ── Left panel — hidden on mobile ──────────────────────── */}
        <aside className="hidden md:block border-r border-stone">
          <div
            className="sticky top-0 p-4 overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - var(--header-height))' }}
          >
            {loading || !quote ? (
              <div className="flex justify-center py-10">
                <LoadingSpinner size="md" />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Back to quotes list */}
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex items-center gap-1 text-[13px] text-slate hover:text-midnight
                             -ml-0.5 transition-colors duration-150"
                >
                  <ChevronLeft size={15} />
                  All Moves
                </button>

                {/* Hero Card */}
                <HeroCard
                  quote={quote}
                  stage={stage}
                  job={job}
                  versionsCount={versionsCount}
                  onStatusClick={() => setShowStatusSheet(true)}
                  onLeadClick={openLeadPicker}
                  onScheduleEstimate={() => setShowEstimateSheet(true)}
                  onLogActivity={() => setShowActivitySheet(true)}
                  onViewComms={() => navigate(`/moves/${quoteId}/communications`)}
                  onLogIssue={() => setShowIssueModal(true)}
                  onStartJob={handleStartJob}
                  startingJob={startingJob}
                  onViewSignOff={() => setShowSignOffDetail(true)}
                  onAdvance={() => setConfirmAdvance(true)}
                  onRevert={() => setConfirmRevert(true)}
                  onRequestSignOff={handleRequestSignOff}
                  onRequestPayment={() => setShowPaymentSheet(true)}
                  advancing={advancing}
                  reverting={reverting}
                  updateQuote={updateQuote}
                  navigate={navigate}
                  trucks={fleetTrucks}
                  warehouses={warehouses}
                  onCheckOutTruck={handleCheckOutTruck}
                  onCheckInTruck={handleCheckInTruck}
                  onAssignTruck={() => setShowHeroTruckAssign(true)}
                />

                {/* Nav */}
                {navItems.length > 0 && (
                  <nav className="flex flex-col gap-2">
                    {navItems.map(item => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          `w-full text-left bg-white border rounded-xl p-3 shadow-brand-sm
                           flex items-center gap-3
                           transition-all duration-150
                           ${isActive
                             ? 'border-plum/40 shadow-brand-md'
                             : 'border-stone hover:border-plum/40 hover:shadow-brand-md active:bg-mist'
                           }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center
                              ${isActive ? 'bg-plum/10' : 'bg-mist'}`}
                            >
                              <span className={isActive ? 'text-plum' : 'text-slate'}>{item.icon}</span>
                            </div>
                            <p className={`flex-1 min-w-0 font-semibold text-[13px] ${isActive ? 'text-plum' : 'text-midnight'}`}>{item.label}</p>
                            <ChevronRight size={16} className="shrink-0 text-slate" />
                          </>
                        )}
                      </NavLink>
                    ))}
                  </nav>
                )}

                {/* Delete quote — intentionally subtle */}
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-1.5 text-[12px] text-slate/60 hover:text-rose
                               transition-colors duration-150"
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* ── Right panel / Mobile full-screen ───────────────────── */}
        <div>
          {orgItemsError && <OrgInventoryErrorBanner message={orgItemsError} />}
          <Outlet />
        </div>

        {/* ── Sheets ─────────────────────────────────────────────── */}
        <BottomSheet open={showStatusSheet} onClose={() => setShowStatusSheet(false)}
          title={`Change ${LIFECYCLE_STAGE_LABEL[stage]} Status`}>
          <StatusSheet
            stage={stage}
            currentStatus={currentStatus}
            onSelect={handleStatusChange}
            onClose={() => setShowStatusSheet(false)}
          />
        </BottomSheet>

        <BottomSheet open={showUserPicker} onClose={() => setShowUserPicker(false)}
          title={userPickerMode === 'moveLead' ? 'Assign Move Lead' : 'Change Quote Lead'}>
          <UserPickerSheet
            users={pickerUsers}
            currentUid={currentLeadUid}
            onSelect={handleLeadAssign}
            onClose={() => setShowUserPicker(false)}
          />
        </BottomSheet>

        {showEstimateSheet && (
          <EstimateScheduledSheet
            users={orgUsers}
            usersLoading={usersLoading}
            onConfirm={handleEstimateScheduled}
            onClose={() => setShowEstimateSheet(false)}
          />
        )}

        {showActivitySheet && quote && (
          <CommunicationLogSheet
            quote={quote}
            updateQuote={updateQuote}
            onClose={() => setShowActivitySheet(false)}
          />
        )}

        {/* Log Issue modal (job stage) */}
        <IssueLogModal
          isOpen={showIssueModal}
          onClose={() => setShowIssueModal(false)}
          onSubmit={async (data: IssueFormData) => {
            await logJobIssue(data);
            setShowIssueModal(false);
          }}
        />

        {/* Truck assignment modal (from hero card no-trucks state) */}
        {showHeroTruckAssign && orgId && quoteId && (
          <TruckAssignmentModal
            orgId={orgId}
            jobId={quoteId}
            moveDate={quote?.movingDate?.preferredDate}
            trucksNeeded={quote?.formula?.truckQuantity ?? 1}
            currentTrucks={job?.assignedTrucks ?? []}
            onSave={async (trucks) => {
              await updateJobTrucks(trucks);
              await refetchTrucks();
              await refetchJob();
            }}
            onClose={() => setShowHeroTruckAssign(false)}
          />
        )}

        {/* Delete confirmation sheet */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
            <div className="absolute inset-0 bg-midnight/50" onClick={deleting ? undefined : () => setShowDeleteConfirm(false)} aria-hidden="true" />
            <div
              className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10
                         md:rounded-2xl md:max-w-sm md:w-full md:pb-6"
            >
              <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-h3 text-midnight">Delete</h2>
                {!deleting && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                               hover:text-midnight hover:bg-mist transition-colors"
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <p className="text-[13px] text-slate leading-snug mb-5">
                Are you sure you want to delete the quote for{' '}
                <span className="font-medium text-midnight">{quote?.customerCompanyName ?? 'this customer'}</span>?
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 h-11 rounded-xl border border-stone text-[13px] font-medium text-midnight
                             hover:bg-mist active:bg-stone/60 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteQuote}
                  disabled={deleting}
                  className="flex-1 h-11 rounded-xl bg-rose text-[13px] font-medium text-white
                             hover:bg-rose/90 active:opacity-80 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting\u2026' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Job Progress Modals ─────────────────────────────────────── */}

        {/* Advance confirmation */}
        <ConfirmModal
          isOpen={confirmAdvance}
          title={job?.jobStage ? (JOB_STAGE_ACTION[job.jobStage] ?? 'Advance Stage') : 'Advance Stage'}
          message={`Are you sure you want to proceed to "${
            job?.jobStage
              ? JOB_STAGE_LABEL[JOB_STAGES[JOB_STAGES.indexOf(job.jobStage) + 1] ?? 'complete']
              : 'next stage'
          }"?`}
          confirmLabel={job?.jobStage ? (JOB_STAGE_ACTION[job.jobStage] ?? 'Continue') : 'Continue'}
          variant="primary"
          onConfirm={handleAdvance}
          onClose={() => setConfirmAdvance(false)}
        />

        {/* Revert confirmation */}
        <ConfirmModal
          isOpen={confirmRevert}
          title="Revert Stage"
          message={`Are you sure you want to go back to "${
            job?.jobStage
              ? JOB_STAGE_LABEL[JOB_STAGES[JOB_STAGES.indexOf(job.jobStage) - 1] ?? 'scheduled']
              : 'previous stage'
          }"?`}
          confirmLabel="Revert"
          variant="danger"
          onConfirm={handleRevert}
          onClose={() => setConfirmRevert(false)}
        />

        {/* Sign-Off Capture Modal */}
        <SignOffCaptureModal
          isOpen={showSignOffCapture}
          onConfirm={handleSignOffCapture}
          onWaive={() => {
            setShowSignOffCapture(false);
            setShowWaiveSignOff(true);
          }}
          onClose={() => setShowSignOffCapture(false)}
          submitting={signOffSubmitting}
          error={signOffError}
        />

        {/* Waive Sign-Off Modal */}
        <WaiveSignOffModal
          isOpen={showWaiveSignOff}
          onConfirm={handleSignOffWaive}
          onClose={() => setShowWaiveSignOff(false)}
          submitting={signOffSubmitting}
        />

        {/* Sign-Off Detail Modal */}
        {job?.customerSignOff && (
          <SignOffDetailModal
            isOpen={showSignOffDetail}
            signOff={job.customerSignOff}
            onCaptureSignOff={() => {
              setShowSignOffDetail(false);
              setShowSignOffCapture(true);
            }}
            onClose={() => setShowSignOffDetail(false)}
          />
        )}

        {/* Payment Request Sheet */}
        {showPaymentSheet && job && orgId && quoteId && (
          <RequestPaymentSheet
            job={job}
            orgId={orgId}
            quoteId={quoteId}
            onSend={handleSendPaymentRequest}
            sending={paymentSending}
            onClose={() => setShowPaymentSheet(false)}
            onReconciled={handleReconciled}
          />
        )}
      </div>
    </QuoteContextProvider>
  );
}
